import { Row, Col, Form, InputGroup } from 'react-bootstrap';
import { UPLOADS_BASE } from '../constants/api';
import {
  filterCitiesForParent,
  filterCountries,
} from '../../../utils/locationHelpers';
import { calculateAgeFromDob } from '../utils/patientFormHelpers';

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

const UserMasterForm = ({
  formData,
  onChange,
  userTypes = [],
  establishments = [],
  locationTypes = [],
  locations = [],
}) => {
  const countries = filterCountries(locations, locationTypes);
  const cityLocations = filterCitiesForParent(locations, locationTypes, formData.country_id);
  const matchingEstablishments = formData.location_id
    ? establishments.filter(
        (est) => String(est.location_id) === String(formData.location_id)
      )
    : [];

  const handleCountryChange = (countryId) => {
    onChange('country_id', countryId);
    onChange('location_id', '');
    onChange('establishment_id', '');
    const country = countries.find((c) => String(c.location_id) === String(countryId));
    onChange('country_code', country?.country_code || '');
  };

  const handleLocationChange = (locationId) => {
    onChange('location_id', locationId);
    onChange('establishment_id', '');
  };

  const handleDobChange = (value) => {
    onChange('date_of_birth', value);
    onChange('age_years', value ? calculateAgeFromDob(value) : '');
  };

  const displayAge = formData.date_of_birth
    ? (formData.age_years || calculateAgeFromDob(formData.date_of_birth))
    : (formData.age_years || '');

  return (
    <div className="em-form">
      <div className="em-section-card">
        <h4 className="em-section-title">
          <span className="em-section-num">1. </span>
          User Information
        </h4>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="userTypeId">
            <Form.Label>User Type <span className="text-danger">*</span></Form.Label>
            <Form.Select
              required
              value={formData.user_type_id || ''}
              onChange={(e) => onChange('user_type_id', e.target.value)}
            >
              <option value="">Select user type</option>
              {userTypes.map((ut) => (
                <option key={ut.user_type_id} value={ut.user_type_id}>
                  {ut.user_type}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
          </Form.Group>

          <Form.Group as={Col} md="6" controlId="userName">
            <Form.Label>Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="e.g. John Doe"
              value={formData.user_name || ''}
              onChange={(e) => onChange('user_name', e.target.value)}
            />
            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="userProfilePic">
            <Form.Label>Profile Pic</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => onChange('profile_pic', e.target.files[0])}
            />
            {(formData.profile_pic instanceof File || formData.profile_pic_path) && (
              <div className="em-image-preview mt-2">
                <img
                  src={
                    formData.profile_pic instanceof File
                      ? URL.createObjectURL(formData.profile_pic)
                      : `${UPLOADS_BASE}/uploads/${formData.profile_pic_path}`
                  }
                  alt="Profile preview"
                  style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }}
                />
              </div>
            )}
          </Form.Group>

          <Form.Group as={Col} md="6" controlId="userGender">
            <Form.Label>Gender</Form.Label>
            <Form.Select
              value={formData.gender || ''}
              onChange={(e) => onChange('gender', e.target.value)}
            >
              <option value="">Select gender</option>
              {GENDER_OPTIONS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="userDob">
            <Form.Label>Date of Birth</Form.Label>
            <Form.Control
              type="date"
              value={formData.date_of_birth || ''}
              onChange={(e) => handleDobChange(e.target.value)}
            />
          </Form.Group>

          <Form.Group as={Col} md="6" controlId="userAge">
            <Form.Label>Age</Form.Label>
            <Form.Control
              type="text"
              readOnly
              className="em-field-locked"
              value={displayAge}
              placeholder="Calculated from date of birth"
            />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="userCountry">
            <Form.Label>Country</Form.Label>
            <Form.Select
              value={formData.country_id || ''}
              onChange={(e) => handleCountryChange(e.target.value)}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.location_id} value={c.location_id}>
                  {c.location_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group as={Col} md="6" controlId="userLocation">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={formData.location_id || ''}
              onChange={(e) => handleLocationChange(e.target.value)}
              disabled={!formData.country_id}
            >
              <option value="">
                {!formData.country_id ? 'Select country first' : 'Select location'}
              </option>
              {cityLocations.map((loc) => (
                <option key={loc.location_id} value={loc.location_id}>
                  {loc.location_name}
                </option>
              ))}
            </Form.Select>
            <span className="em-field-hint">City/District locations from Location Master for selected country</span>
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="userPhone">
            <Form.Label>Phone Number</Form.Label>
            <InputGroup>
              <InputGroup.Text className={formData.country_code ? '' : 'text-muted'}>
                {formData.country_code || 'Code'}
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={formData.country_id ? 'Phone number' : 'Select country first'}
                value={formData.phone_number || ''}
                onChange={(e) => onChange('phone_number', e.target.value)}
                disabled={!formData.country_id}
              />
            </InputGroup>
            <span className="em-field-hint">Country code is set automatically from country selection</span>
          </Form.Group>

          <Form.Group as={Col} md="6" controlId="userEmail">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              placeholder="email@example.com"
              value={formData.email_address || ''}
              onChange={(e) => onChange('email_address', e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="userEstablishment">
            <Form.Label>Establishment / Hospital Name</Form.Label>
            <Form.Select
              value={formData.establishment_id || ''}
              onChange={(e) => onChange('establishment_id', e.target.value)}
              disabled={!formData.location_id}
            >
              <option value="">
                {!formData.location_id ? 'Select location first' : 'None (optional)'}
              </option>
              {matchingEstablishments.map((est) => (
                <option key={est.establishment_id} value={est.establishment_id}>
                  {est.establishment_name}
                </option>
              ))}
            </Form.Select>
            <span className="em-field-hint">Only establishments matching the selected location</span>
          </Form.Group>

          <Form.Group as={Col} md="6" controlId="userComments">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Optional notes"
              value={formData.comments || ''}
              onChange={(e) => onChange('comments', e.target.value)}
            />
          </Form.Group>
        </Row>
      </div>
    </div>
  );
};

export default UserMasterForm;
